import {makeAutoObservable, reaction} from "mobx";
import {KeyValue} from "./keyValue";

export class ObservableList<TItem extends KeyValue> {
  private heldValue: string | null = null;
  selectedItem: TItem | null;
  items: TItem[];
  validate?: (toValidate: TItem | null) => boolean;
  noItemsMatchDefaultTo?: () => string;
  additionalItems?: TItem[];
  isDirty: boolean;
  initialValue?: string | null;
  constructor(
    items: TItem[],
    selectedItemValue?: string | null,
    validate?: (toValidate: TItem | null) => boolean,
    noItemsMatchDefaultTo?: () => string,
    additionalItems?: TItem[]
  ) {
    this.initialValue = null;
    makeAutoObservable(this);
    this.items = items;
    if (this.items.length === 0 && selectedItemValue) {
      this.heldValue = selectedItemValue;
      this.selectedItem = null;
    } else if (selectedItemValue) {
      const selectedValue =
        this.items.find((i) => i.value === selectedItemValue) ?? null;
      this.selectedItem = selectedValue;
      this.initialValue = selectedValue?.value;
    } else {
      this.selectedItem = null;
    }

    this.validate = validate;
    this.noItemsMatchDefaultTo = noItemsMatchDefaultTo;
    this.additionalItems = additionalItems;
    this.isDirty = false;
    reaction(
      () => this.items,
      () => {
        if (this.heldValue !== null && this.selectedItem === null) {
          this.setSelectedItem(this.heldValue);
          this.initialValue = this.heldValue;
          this.heldValue = null;
        }
      }
    );
  }

  setIsDirty = (value: boolean) => {
    this.isDirty = value;
  };

  get isValid() {
    return (
      this.validate == null || !this.isDirty || this.validate(this.selectedItem)
    );
  }

  setSelectedItem = (value: string | null) => {
    if (value === null) {
      this.selectedItem = null;
      return;
    }
    let toSelect = this.items.find((i) => i.value === value) ?? null;
    if (toSelect === null && this.noItemsMatchDefaultTo !== undefined) {
      toSelect =
        this.items.find(
          (i) =>
            this.noItemsMatchDefaultTo !== undefined &&
            i.value === this.noItemsMatchDefaultTo()
        ) ?? null;
    }
    this.selectedItem = toSelect;
    if (this.heldValue == null) {
      this.setIsDirty(true);
    }
  };

  setItems = (items: TItem[]) => {
    this.items = items;
    this.additionalItems?.forEach((a) => this.items.push(a));
  };

  setInitialValue = () => {
    this.initialValue = this.selectedItem?.value;
  };

  get hasChange(): boolean {
    return this.selectedItem?.value !== this.initialValue;
  }
}
